/* ═════════════════════════════════════════════════════════════════════
   Phase data — content per menstrual phase.
   In Phase 1 this is static, human-written. Later this will be
   populated from Supabase cycle records + AI interpretation.
   ═════════════════════════════════════════════════════════════════════ */

export const PHASES = {
  menstrual: {
    id: 'menstrual',
    label: 'Bleed',
    tag: 'Cycle Day',
    day: 2,
    of: '/ 28',
    name: 'Menstrual Phase',
    sentence: 'Your body is shedding. Rest, warmth and iron will help you today.',
    card: {
      day: '2',
      meta: [
        { primary: 'Period · day 2 of 5', kind: 'strong' },
        { primary: 'Started 14 Feb',      kind: 'mute' },
      ],
      confidence: 'High',
      confidenceOn: 3,
    },
    body: {
      energy: 'Low', mood: 'Tender', recovery: 88,
      focus: 'Warm meals and gentle movement.',
    },
    waiting: false,
    cta: { label: 'Update period', variant: 'ghost' },
  },
  follicular: {
    id: 'follicular',
    label: 'Rise',
    tag: 'Cycle Day', day: 8, of: '/ 28',
    name: 'Follicular Phase',
    sentence: 'Estrogen is climbing. Your energy is returning — a good day to plan.',
    card: {
      day: '8',
      meta: [
        { primary: 'Next in 13 days',  kind: 'strong' },
        { primary: 'Expected 28 Feb',  kind: 'mute' },
      ],
      confidence: 'High', confidenceOn: 3,
    },
    body: { energy: 'Rising', mood: 'Open', recovery: 79,
      focus: 'Move your body — cardio lands well now.' },
    waiting: false,
    cta: { label: 'Log period', variant: 'ghost' },
  },
  ovulatory: {
    id: 'ovulatory',
    label: 'Peak',
    tag: 'Cycle Day', day: 14, of: '/ 28',
    name: 'Ovulation',
    sentence: 'Your body appears to be entering its highest-energy phase.',
    card: {
      day: '14',
      meta: [
        { primary: 'Next in 7 days',  kind: 'strong' },
        { primary: 'Expected 22 Feb', kind: 'mute' },
      ],
      confidence: 'Very High', confidenceOn: 3,
    },
    body: { energy: 'Peak', mood: 'Bright', recovery: 91,
      focus: 'High-value work, difficult conversations.' },
    waiting: false,
    cta: { label: 'Log period', variant: 'ghost' },
  },
  luteal: {
    id: 'luteal',
    label: 'Luteal',
    tag: 'Cycle Day', day: 21, of: '/ 28',
    name: 'Luteal Phase',
    sentence: 'Your body is preparing itself. You may feel calmer today, and slightly more tender.',
    card: {
      day: '21',
      meta: [
        { primary: 'Next in 7 days',  kind: 'strong' },
        { primary: 'Expected 22 Feb', kind: 'mute' },
      ],
      confidence: 'High', confidenceOn: 3,
    },
    body: { energy: 'High', mood: 'Calm', recovery: 82,
      focus: 'Stay hydrated and avoid caffeine after noon.' },
    waiting: false,
    cta: { label: 'Log period', variant: 'ghost' },
  },
  waiting: {
    id: 'waiting',
    label: 'Late',
    tag: 'Cycle Day', day: 34, of: '',
    name: 'Awaiting Period',
    sentence: 'Your cycle is 5 days longer than usual. This is common with PCOS — I\'ll keep counting.',
    card: {
      day: '34',
      meta: [
        { primary: 'Expected 5 days ago', kind: 'late' },
        { primary: '10 Feb · log when it starts', kind: 'mute' },
      ],
      confidence: 'Medium', confidenceOn: 2,
      waitingStrip: 'Cycle continues past expected date — I\'ll keep counting.',
    },
    body: { energy: 'Low', mood: 'Muted', recovery: 71,
      focus: 'Warmth and rest. Ovulation may have been delayed.' },
    waiting: true,
    cta: { label: 'Log period now', variant: 'warning' },
  },
};

export const PHASE_ORDER = ['menstrual', 'follicular', 'ovulatory', 'luteal', 'waiting'];

/* ─── Today's Intelligence content (phase-driven) ─── */
/* ─── Today's Intelligence content (phase-driven).
     Structured for scannable card-based rendering: hero, hormone chips,
     experience cards, single action recommendation, pattern insights,
     Theo interpretation. ─── */

/* Phase-agnostic scannable content is derived from the classic content
   below plus these lightweight structured maps for cards. All fields
   are optional — the screen falls back to the classic text if absent. */

export const INTELLIGENCE_CARDS = {
  menstrual: {
    hormones: [
      { name: 'Estrogen',    level: 'Low',    dir: 'down', tone: 'quiet' },
      { name: 'Progesterone',level: 'Low',    dir: 'down', tone: 'quiet' },
      { name: 'FSH',         level: 'Rising', dir: 'up',   tone: 'brand' },
    ],
    experience: [
      { k: 'Energy',   v: 'Low',    r: 'Iron dipping',              tint: 'quiet'  },
      { k: 'Mood',     v: 'Tender', r: 'Estrogen at cycle-low',     tint: 'brand'  },
      { k: 'Sleep',    v: 'Deep',   r: 'Temperature back to baseline', tint: 'brand' },
      { k: 'Recovery', v: '88%',    r: 'Above your Day-2 avg',      tint: 'brand'  },
    ],
    action: { title: 'Warm meals + earlier sleep', tag: 'Today',
      science: 'Warm food eases prostaglandin cramping and supports slower digestion. Sleep is where most of your recovery happens during a bleed.' },
    patterns: [
      { title: 'Cramping usually eases by Day 3', confidence: 'High',   observed: '4 cycles' },
      { title: 'Your period lengths cluster at 4–5 days', confidence: 'High', observed: '6 cycles' },
    ],
  },
  follicular: {
    hormones: [
      { name: 'Estrogen',     level: 'Rising',  dir: 'up',   tone: 'brand' },
      { name: 'FSH',          level: 'Peaking', dir: 'up',   tone: 'brand' },
      { name: 'Progesterone', level: 'Low',     dir: 'flat', tone: 'quiet' },
    ],
    experience: [
      { k: 'Energy',   v: 'Rising', r: '+18% since Day 5',    tint: 'brand' },
      { k: 'Mood',     v: 'Open',   r: 'Serotonin lifting',    tint: 'brand' },
      { k: 'Sleep',    v: 'Steady', r: 'Deep sleep stable',   tint: 'brand' },
      { k: 'Recovery', v: '79%',    r: 'Trending upward',      tint: 'brand' },
    ],
    action: { title: 'Plan the hard work this week', tag: 'This week',
      science: 'Follicular estrogen amplifies focus, memory, and cardio recovery. This is the physiological window for new work, difficult meetings, and creative launches.' },
    patterns: [
      { title: 'Ovulation lands Day 13–15 for you', confidence: 'High', observed: '3 cycles' },
      { title: 'Cardio recovery is +12% vs luteal', confidence: 'Med',  observed: '2 cycles' },
    ],
  },
  ovulatory: {
    hormones: [
      { name: 'LH',           level: 'Surged',   dir: 'up',   tone: 'brand' },
      { name: 'Estrogen',     level: 'Peaked',   dir: 'down', tone: 'brand' },
      { name: 'Progesterone', level: 'Rising',   dir: 'up',   tone: 'brand' },
    ],
    experience: [
      { k: 'Energy',   v: 'Peak',   r: 'Cycle maximum',       tint: 'brand' },
      { k: 'Mood',     v: 'Bright', r: 'Dopamine + serotonin', tint: 'brand' },
      { k: 'Sleep',    v: 'Light',  r: 'Temperature rising',  tint: 'quiet' },
      { k: 'Recovery', v: '91%',    r: 'Cycle high',          tint: 'brand' },
    ],
    action: { title: 'Say the thing today', tag: 'Today',
      science: 'Verbal fluency peaks with the LH surge. Your voice literally carries more musically this week. Difficult conversations tend to land better.' },
    patterns: [
      { title: 'You feel most confident on ovulation days', confidence: 'High', observed: '4 cycles' },
      { title: 'Cervical mucus consistently signals your peak', confidence: 'High', observed: '5 cycles' },
    ],
  },
  luteal: {
    hormones: [
      { name: 'Progesterone', level: 'Peak',    dir: 'up',   tone: 'brand' },
      { name: 'Estrogen',     level: 'Falling', dir: 'down', tone: 'quiet' },
      { name: 'LH',           level: 'Quiet',   dir: 'flat', tone: 'quiet' },
    ],
    experience: [
      { k: 'Energy',      v: 'High',      r: 'Above your Day-21 baseline', tint: 'brand' },
      { k: 'Mood',        v: 'Calm',      r: 'Progesterone dominant',       tint: 'brand' },
      { k: 'Sleep',       v: 'Lighter',   r: 'Core temp +0.4°C',            tint: 'quiet' },
      { k: 'Temperature', v: '+0.4°C',    r: 'Above baseline',              tint: 'quiet' },
      { k: 'Recovery',    v: '82%',       r: 'Sleep was 43m shorter',       tint: 'quiet' },
      { k: 'Focus',       v: 'Softer',    r: 'Progesterone slows tempo',    tint: 'quiet' },
    ],
    action: { title: 'Cool your room by 2°C tonight', tag: 'Tonight',
      science: 'Luteal core temperature runs 0.4°C higher, which fragments deep sleep. Room temperature is the single biggest lever this week — I\'ve tracked the effect on your recovery score.' },
    patterns: [
      { title: 'Chest tenderness appears Day 21–23',          confidence: 'High', observed: '3 cycles' },
      { title: 'Deep sleep drops during last week of cycle',  confidence: 'High', observed: '5 cycles' },
      { title: 'Insulin sensitivity is 18% lower this week',  confidence: 'Med',  observed: '2 cycles' },
    ],
  },
  waiting: {
    hormones: [
      { name: 'Progesterone', level: 'Elevated', dir: 'down', tone: 'brand' },
      { name: 'Estrogen',     level: 'Low',      dir: 'flat', tone: 'quiet' },
      { name: 'LH',           level: 'Quiet',    dir: 'flat', tone: 'quiet' },
    ],
    experience: [
      { k: 'Energy',   v: 'Low',    r: 'Prolonged luteal',       tint: 'quiet' },
      { k: 'Mood',     v: 'Muted',  r: 'Pre-bleed sensitivity',  tint: 'quiet' },
      { k: 'Sleep',    v: 'Light',  r: '3 nights of lower deep', tint: 'quiet' },
      { k: 'Recovery', v: '71%',    r: 'Slightly reduced',       tint: 'quiet' },
    ],
    action: { title: 'Warmth, rest, less caffeine', tag: '48 hours',
      science: 'Cortisol interacts with progesterone withdrawal. Reducing caffeine tends to accelerate the bleed by 24–48 hours. Warm baths and earlier bedtimes soften the nervous-system edge.' },
    patterns: [
      { title: 'Your 2nd extended cycle this year', confidence: 'High', observed: 'This year' },
      { title: 'Basal temperature stayed elevated', confidence: 'High', observed: 'Confirms ovulation' },
    ],
  },
};

export const INTELLIGENCE = {
  menstrual: {
    hero: {
      eyebrow: 'Menstrual · Day 2',
      title: 'Your body is doing <em>quiet, brave work</em> today.',
      sub: 'The uterine lining is shedding. Prostaglandins are elevated, which explains any cramping. Iron is starting to dip. Everything you feel today has a physiological reason.',
    },
    stats: [
      { v: 'Low',  k: 'Energy',   r: 'Uterine work is metabolically expensive.', accent: true },
      { v: 'Tender', k: 'Mood',   r: 'Estrogen is at its lowest point of the cycle.' },
      { v: '88', u: '%', k: 'Recovery', r: 'Sleep quality was strong — your body is prioritising repair.' },
    ],
    hormonal: {
      title: 'The hormonal picture',
      body: [
        '<b>Estrogen</b> and <b>progesterone</b> are both at their lowest — this is by design. The withdrawal is what triggers the bleed itself.',
        'By tomorrow, follicle-stimulating hormone will begin to rise, quietly starting the next cycle inside you.',
      ],
    },
    cycle: {
      title: 'Why this is happening',
      body: [
        'You started bleeding on <b>14 February</b>. This is day 2 of a typical 4–6 day period for you. Everything is on rhythm.',
        'Cramping usually eases by day 3. If it doesn\'t, or if flow feels heavier than usual, that\'s worth logging.',
      ],
    },
    observations: [
      { title: 'Sleep improved by 47 min last night',    desc: 'This is common on day 2 — your body drops core temperature back to baseline once the bleed begins.' },
      { title: 'Iron is likely trending down',           desc: 'You\'re losing an estimated 20–40mg of iron across this period. This matters most on day 3 and 4.' },
      { title: 'Skin may feel more sensitive',           desc: 'Low estrogen thins the skin\'s protective lipid layer. Use gentler cleansers this week.' },
    ],
    priorities: [
      { title: 'Warm, iron-rich lunch',        desc: 'Lentils, eggs, or dark greens. Warmth is easier for your slower digestion right now.' },
      { title: 'Move gently, not hard',         desc: 'A walk lifts prostaglandin cramps better than a hard workout does this week.' },
      { title: 'Give yourself an earlier bedtime', desc: 'Deep sleep during your bleed is where most of your recovery happens.' },
    ],
    educational: {
      title: 'A small thing worth knowing',
      body: 'PCOS often changes flow duration and intensity across cycles. If your period feels different this month — heavier, lighter, longer — that\'s data, not something wrong. I\'ll track it.',
    },
    interpretation: 'You are at the deepest inward point of your cycle. Everything you feel today — the tenderness, the tiredness, the small emotional edge — is your body concentrating its energy on <em>renewal, not decline</em>. Rest is the work today.',
    confidence: 'High',
  },
  follicular: {
    hero: {
      eyebrow: 'Follicular · Day 8',
      title: 'Something is <em>beginning inside you.</em>',
      sub: 'Estrogen is climbing steadily. The follicular phase is your body\'s constructive season — rebuilding the endometrial lining and priming an egg for release later this week.',
    },
    stats: [
      { v: 'Rising', k: 'Energy', r: 'Estrogen is at 62% of its cycle-high.', accent: true },
      { v: 'Open',   k: 'Mood',   r: 'Serotonin uptake improves as estrogen rises.' },
      { v: '79', u: '%', k: 'Recovery', r: 'Recovery capacity is trending up — a good week to train.' },
    ],
    hormonal: {
      title: 'The hormonal picture',
      body: [
        '<b>Estrogen</b> has more than doubled since your period ended. <b>FSH</b> is quietly selecting a dominant follicle right now.',
        '<b>LH</b> will surge in about 5–7 days, triggering ovulation. That\'s when your peak clarity window opens.',
      ],
    },
    cycle: {
      title: 'Where you are in this cycle',
      body: [
        'You are 8 days into this cycle. If the pattern holds, ovulation is expected around <b>Day 14</b>.',
        'Your average follicular length across the last three cycles is 13 days — steady, well within the healthy range.',
      ],
    },
    observations: [
      { title: 'Insulin sensitivity is at its cycle-high',  desc: 'This is the best week for carbohydrate tolerance. Your body handles complex carbs better right now.' },
      { title: 'Cardio recovery is improved',                desc: 'Estrogen supports aerobic capacity. VO₂-style work responds well this week.' },
      { title: 'Skin barrier is strengthening',              desc: 'Higher estrogen means better hydration retention. Actives (retinoids, acids) are better tolerated.' },
    ],
    priorities: [
      { title: 'Plan the harder work',       desc: 'Difficult meetings, learning, creative launches — your brain has more bandwidth for novelty this week.' },
      { title: 'Add a strength session',      desc: 'Rising estrogen amplifies protein synthesis. Muscle-building responds especially well right now.' },
      { title: 'Front-load your carbohydrates', desc: 'Insulin sensitivity peaks — use it for whole grains, fruit, legumes rather than saving them for later phases.' },
    ],
    educational: {
      title: 'A small thing worth knowing',
      body: 'With PCOS, the follicular phase can be longer or more irregular than typical. If ovulation hasn\'t happened by day 20, that\'s useful information — I\'ll flag it, not alarm you.',
    },
    interpretation: 'Your body is in <em>constructive mode</em>. Estrogen is rebuilding you cell by cell — tissue, mood, energy, immunity. This is the week to trust the returning momentum. It\'s safe to want things again.',
    confidence: 'High',
  },
  ovulatory: {
    hero: {
      eyebrow: 'Ovulation · Day 14',
      title: 'Your body is at its <em>most receptive</em> today.',
      sub: 'You are within your ovulatory window. The LH surge has released an egg and your body is at its physiological peak — brain, muscle and mood all lifted by this specific hormonal moment.',
    },
    stats: [
      { v: 'Peak', k: 'Energy',   r: 'Your highest of the cycle. It will not last.', accent: true },
      { v: 'Bright', k: 'Mood',   r: 'Dopamine and serotonin are both elevated.' },
      { v: '91', u: '%', k: 'Recovery', r: 'Recovery is at cycle-high. Ideal for peak output.' },
    ],
    hormonal: {
      title: 'The hormonal picture',
      body: [
        'The <b>LH surge</b> happened yesterday. <b>Estrogen</b> peaked just before it and is now beginning to fall.',
        '<b>Progesterone</b> starts rising today from the corpus luteum. This transition is why some feel a mid-cycle mood shift — it is chemical, not personal.',
      ],
    },
    cycle: {
      title: 'Where you are in this cycle',
      body: [
        'Day 14 of a typical 28-day pattern. The 3-day ovulatory window opened yesterday and closes on <b>Day 16</b>.',
        'Basal body temperature will rise 0.3–0.6°C over the next 48 hours, confirming ovulation retrospectively.',
      ],
    },
    observations: [
      { title: 'Verbal fluency is at its cycle peak',    desc: 'Estrogen enhances left-hemisphere activity. This is the week you speak clearest and most confidently.' },
      { title: 'Pain tolerance is elevated',              desc: 'Ovulatory endorphins make hard workouts feel easier. Use it — but don\'t push into injury.' },
      { title: 'Cervical mucus is at its most fertile',   desc: 'A key marker of the ovulatory window. Body temperature will confirm within 48 hours.' },
    ],
    priorities: [
      { title: 'Say the thing you\'ve been rehearsing',    desc: 'Your voice literally carries more musically this week. Difficult conversations land better.' },
      { title: 'Peak effort work this morning',            desc: 'Cognitive throughput is at cycle-high. Save admin for the afternoon.' },
      { title: 'Hydrate more than yesterday',              desc: 'Ovulatory temperature rise increases fluid loss by roughly 200ml over the day.' },
    ],
    educational: {
      title: 'A small thing worth knowing',
      body: 'PCOS can suppress ovulation in some cycles without eliminating it entirely. If you feel the ovulatory shift (energy, mood, temperature), that\'s meaningful — I\'ll track its consistency.',
    },
    interpretation: 'This is your <em>momentum window</em>. Everything your body does today is designed for outward action — connection, expression, achievement. Take advantage of the current, but plan for the wave to pass by the weekend.',
    confidence: 'Very High',
  },
  luteal: {
    hero: {
      eyebrow: 'Luteal · Day 21',
      title: 'Your body is folding inward, <em>gathering itself.</em>',
      sub: 'You are in the second half of your luteal phase. Progesterone is at its cycle peak, which is why you feel calmer, warmer, and slightly more tender. Softness today is not weakness — it is the work.',
    },
    stats: [
      { v: 'High', k: 'Energy',   r: 'Cycle-baseline for Day 21 is 58% — you are above.', accent: true },
      { v: 'Calm', k: 'Mood',     r: 'Progesterone is at its peak. It slows on purpose.' },
      { v: '82', u: '%', k: 'Recovery', r: 'Warmer core temperature disrupted deep sleep.' },
    ],
    hormonal: {
      title: 'The hormonal picture',
      body: [
        '<b>Progesterone</b> is at 22 ng/mL (peak). This is the hormone doing most of the work you feel today — the calm, the warmth, the mild swelling.',
        '<b>Estrogen</b> has fallen 40% from its ovulatory peak. This drop is why some notice mood dips or sleep disruption around this day.',
      ],
    },
    cycle: {
      title: 'Where you are in this cycle',
      body: [
        'Day 21 of a 28-day cycle. Your period is expected in <b>7 days</b> on 22 February, based on your last three cycles.',
        'The luteal length is your most stable metric — it\'s varied by only 1 day across your last four cycles. That is a very healthy sign.',
      ],
    },
    observations: [
      { title: 'Chest tenderness pattern noted',         desc: 'This has appeared 5–7 days pre-bleed for three cycles in a row. Predictable, not concerning.' },
      { title: 'Sleep was 43 minutes shorter last night', desc: 'Luteal core temperature is 0.4°C higher, which fragments deep sleep. Cooler room helps most.' },
      { title: 'Insulin sensitivity has dropped 18%',     desc: 'Common in the luteal phase. Complex carbs and protein pair better this week than fast sugars.' },
    ],
    priorities: [
      { title: 'Cool your room by two degrees tonight',   desc: 'The single biggest lever you have for better deep sleep this week — I\'ve tracked the effect.' },
      { title: 'Warm, iron-rich lunch',                    desc: 'Your body is quietly preparing either way. Ferritin support now smooths next week\'s bleed.' },
      { title: 'A twelve-minute walk after three o\'clock', desc: 'Late-afternoon light lifts luteal mood better than morning light does for you.' },
    ],
    educational: {
      title: 'A small thing worth knowing',
      body: 'A stable luteal length (11–14 days) is one of the strongest markers that ovulation is happening consistently. Yours is 13 days, and it has barely varied. That is a very good sign for anyone with PCOS.',
    },
    interpretation: 'Your body is in <em>protective mode</em>. Progesterone slows the tempo on purpose — fewer decisions, warmer food, earlier sleep. This is not withdrawal. It\'s your body being smart with its resources before the next cycle begins.',
    confidence: 'High',
  },
  waiting: {
    hero: {
      eyebrow: 'Awaiting · Day 34',
      title: 'Your cycle is longer than usual — <em>I\'m keeping count.</em>',
      sub: 'You are 5 days past your expected period. This is common in PCOS and does not indicate anything wrong. Your body may have ovulated later than usual, which pushes the whole cycle back proportionally.',
    },
    stats: [
      { v: 'Low',   k: 'Energy',   r: 'Prolonged luteal — progesterone still elevated.', accent: true },
      { v: 'Muted', k: 'Mood',     r: 'Anticipatory hormonal state — expected here.' },
      { v: '71', u: '%', k: 'Recovery', r: 'Slightly reduced. Sleep has been lighter for 3 nights.' },
    ],
    hormonal: {
      title: 'The hormonal picture',
      body: [
        '<b>Progesterone</b> is still elevated but starting to withdraw. Once it drops fully, your period will begin — usually within 48–72 hours.',
        '<b>LH</b> is quiet. There is no second ovulation happening — this is simply a delayed onset, not a missed cycle.',
      ],
    },
    cycle: {
      title: 'Why this happens',
      body: [
        'With PCOS, ovulation can shift by several days depending on stress, sleep and insulin dynamics. Your ovulation this cycle was likely on <b>Day 19–20</b> instead of Day 14.',
        'This means your luteal phase length is normal — the cycle is just longer because ovulation came later.',
      ],
    },
    observations: [
      { title: 'This is your 2nd extended cycle this year', desc: 'Extended cycles happened in October (36 days) and now this one. Two in six months is within your typical range.' },
      { title: 'Basal temperature has stayed elevated',     desc: 'This confirms ovulation did happen — you are just waiting for progesterone to withdraw.' },
      { title: 'No signs of a skipped cycle',                desc: 'A skipped cycle would show a temperature drop and different symptom pattern. That is not what I\'m seeing.' },
    ],
    priorities: [
      { title: 'Warmth and rest',                     desc: 'The pre-bleed nervous system is more sensitive. Warm food, warm baths, earlier bedtimes.' },
      { title: 'Log the moment your bleed starts',     desc: 'The exact hour matters for pattern accuracy. I\'ll update everything the moment you log it.' },
      { title: 'Skip caffeine for 48 hours if you can', desc: 'Cortisol interacts with progesterone withdrawal. Less caffeine tends to bring the bleed on faster.' },
    ],
    educational: {
      title: 'A small thing worth knowing',
      body: 'A 34-day cycle is not a warning sign on its own. In fact, up to 30% of cycles for people with PCOS are 30+ days. What matters is whether ovulation happened — and yours did.',
    },
    interpretation: 'Your body is in <em>a longer version of the same cycle,</em> not a broken one. Progesterone is winding down and your period will follow. Nothing here is missing — the timing is just wider than average this month.',
    confidence: 'Medium',
  },
};

/* ─── Recent cycles (static seed for Phase 1) ─── */
export const RECENT_CYCLES = [
  { month: 'June',  range: 'Jun 12 → Jun 16', length: '28d' },
  { month: 'May',   range: 'May 14 → May 18', length: '31d' },
  { month: 'April', range: 'Apr 17 → Apr 21', length: '27d' },
];

/* ─── Placeholder screen definitions ─── */
export const PLACEHOLDER_SCREENS = {
  'body-snapshot':   { title: 'Body Snapshot',    eyebrow: 'Your body · today',   body: 'A complete daily view of your energy, symptoms and recovery — with the reasoning behind each signal.' },
  'symptoms':        { title: 'Symptoms',          eyebrow: 'Track · learn',      body: 'Log symptoms as they arrive, and see the patterns your body has been quietly forming across your cycles.' },
  'hormone-trends':  { title: 'Hormone Trends',    eyebrow: 'Estrogen · Progesterone · LH', body: 'A calm view of the hormones behind everything you feel — and how yours are moving across the month.' },
  'pattern-insights':{ title: 'Pattern Insights',  eyebrow: 'What Theo has learned', body: 'The patterns your body repeats across cycles — surfaced by Theo when they become meaningful.' },
  'smart-suggestions':{title: 'Smart Suggestions', eyebrow: 'Three small things', body: 'Personalized daily nudges from Theo — grounded in your real data, never generic wellness advice.' },
  'cycle-timeline':  { title: 'Cycle Timeline',    eyebrow: 'Bleed · Rise · Peak · Luteal', body: 'A gentle arc of the current cycle — where you are, where you\'ve been, and what tends to come next.' },
  'recent-cycles':   { title: 'Recent Cycles',    eyebrow: 'Your full history',  body: 'Every cycle you\'ve tracked — lengths, patterns, and how your body is evolving over time.' },
  'reports':         { title: 'Reports',           eyebrow: 'Monthly · shareable',body: 'Clinician-ready summaries of your cycle, symptoms and trends — beautifully formatted, always yours to share.' },
  'settings':        { title: 'Settings',          eyebrow: 'Notifications · Data · Privacy', body: 'Fine-tune what Theo notifies you about, how your data is used, and how the experience is personalized.' },
};
